/**
 * Power BI Embed Service — Multi-select filter mappings
 *
 *   Date:         Dimdates[CalendarYear], Dimdates[MonthLongName]
 *   Product:      Product[Category], Product[Subcategory]
 *   Region:       Region[Group], Region[Country]
 *   Salesperson:  Salesperson[Salesperson]
 *   YTD / MTD:    Period[PeriodType]
 */

const API_BASE = process.env.REACT_APP_API_BASE || '';

// ── Embed config ──────────────────────────────────────────────────

export async function getEmbedConfig() {
  const res = await fetch(`${API_BASE}/api/embed-token`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Embed token request failed (${res.status})`);
  }
  return res.json();
}

// ── Filter builder ────────────────────────────────────────────────

export function buildBasicFilter(table, column, operator, values) {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table, column },
    operator,
    values,
    filterType: 1,
  };
}

// ── Collect values from a drilldown selection ─────────────────────
//    value shape: { parents: string[], children: { [parent]: string[] } }

function collectParentValues(sel) {
  if (!sel) return [];
  return [...(sel.parents || [])];
}

function collectChildValues(sel) {
  if (!sel) return [];
  const all = [];
  const children = sel.children || {};
  Object.entries(children).forEach(([parent, arr]) => {
    // Only include children that aren't already covered by a parent selection
    if (!(sel.parents || []).includes(parent)) {
      all.push(...arr);
    }
  });
  return all;
}

function collectAllParents(sel) {
  // All parents that have any selection (fully or partially)
  if (!sel) return [];
  const set = new Set(sel.parents || []);
  Object.keys(sel.children || {}).forEach((p) => set.add(p));
  return [...set];
}

// ── Apply filters ─────────────────────────────────────────────────

export async function applyReportFilters(report, filters) {
  if (!report) return;

  const pbiFilters = [];

  // ── Date ──
  const dateParents = collectAllParents(filters.period);
  if (dateParents.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Dimdates', 'CalendarYear', 'In', dateParents.map(Number))
    );
  }
  const dateChildren = collectChildValues(filters.period);
  if (dateChildren.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Dimdates', 'MonthLongName', 'In', dateChildren)
    );
  }

  // ── Product ──
  const productParents = collectParentValues(filters.product);
  if (productParents.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Product', 'Category', 'In', productParents)
    );
  }
  const productChildren = collectChildValues(filters.product);
  if (productChildren.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Product', 'Subcategory', 'In', productChildren)
    );
  }

  // ── Region ──
  const regionParents = collectParentValues(filters.region);
  if (regionParents.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Region', 'Group', 'In', regionParents)
    );
  }
  const regionChildren = collectChildValues(filters.region);
  if (regionChildren.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Region', 'Country', 'In', regionChildren)
    );
  }

  // ── Salesperson ──
  if (filters.salesperson && filters.salesperson.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Salesperson', 'Salesperson', 'In', filters.salesperson)
    );
  }

  // ── Period Type (YTD / MTD) ──
  if (filters.periodType) {
    pbiFilters.push(
      buildBasicFilter('Period', 'PeriodType', 'In', [filters.periodType])
    );
  }

  try {
    await report.setFilters(pbiFilters);
  } catch (err) {
    console.error('Failed to apply Power BI filters:', err);
  }
}

// ── Page navigation ───────────────────────────────────────────────

export async function navigateToPage(report, pageName) {
  if (!report) return;

  try {
    const pages = await report.getPages();
    const target = pages.find(
      (p) => p.displayName.toLowerCase() === pageName.toLowerCase()
    );
    if (target) {
      await target.setActive();
    }
  } catch (err) {
    console.error('Failed to navigate page:', err);
  }
}
